import { formatPhoneInput, toE164, isValidPhone } from '../../utils/formatPhone'
import { formatSalaryInput } from '../../utils/formatCurrency'
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  TextField,
  Button,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Avatar,
  Tabs,
  Tab,
} from '@mui/material'
import {
  Close as CloseIcon,
  Save as SaveIcon,
  AutoAwesome as AIIcon,
  Send as SendIcon,
  Visibility as ViewIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material'
import { useState, useEffect } from 'react'
import { Lead, LeadStatus, STATUS_LABELS, STATUS_COLORS, LEAD_STATUSES } from '../../state/leads'
import { leadsApi, packagesApi, usersApi, commentsApi, Package, User, Comment } from '../../services/api'
import { useNotification } from '../../hooks/useNotification'
import AttachmentsTab from '../client-detail/AttachmentsTab'

interface LeadDetailPanelProps {
  open: boolean
  lead: Lead | null
  onClose: () => void
  onSave: (lead: Lead) => void
  onQualify: (leadId: string, qualified: boolean, notes: string) => void
  onConvert: (leadId: string) => void
  infoOnly?: boolean
}

interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
    </div>
  )
}

export default function LeadDetailPanel({
  open,
  lead,
  onClose,
  onSave,
  onQualify,
  onConvert,
  infoOnly = false,
}: LeadDetailPanelProps) {
  const [editedLead, setEditedLead] = useState<Lead | null>(null)
  const [qualificationNotes, setQualificationNotes] = useState('')
  const [generatingProposal, setGeneratingProposal] = useState(false)
  const [proposal, setProposal] = useState<string | null>(null)
  const [sendingProposal, setSendingProposal] = useState(false)
  const [proposalSentAt, setProposalSentAt] = useState<string | null>(null)
  const [proposalModalOpen, setProposalModalOpen] = useState(false)
  const [packages, setPackages] = useState<Package[]>([])
  const [loadingPackages, setLoadingPackages] = useState(true)
  const [users, setUsers] = useState<User[]>([])
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [comments, setComments] = useState<Comment[]>([])
  const [newComment, setNewComment] = useState('')
  const [submittingComment, setSubmittingComment] = useState(false)
  const [activeTab, setActiveTab] = useState(0)
  const { showSuccess, showError } = useNotification()

  // Fetch packages and users on mount
  useEffect(() => {
    async function fetchPackages() {
      try {
        const result = await packagesApi.list({ status: 'active' })
        setPackages(result)
      } catch (error) {
        console.error('Error loading packages:', error)
      } finally {
        setLoadingPackages(false)
      }
    }

    async function fetchUsers() {
      try {
        const result = await usersApi.list({ status: 'active' })
        // Filter to only show users who can own leads (sales, managers, admins)
        const eligibleUsers = result.filter((u) =>
          ['admin', 'manager', 'sales', 'client_success'].includes(u.role)
        )
        setUsers(eligibleUsers)
      } catch (error) {
        console.error('Error loading users:', error)
      } finally {
        setLoadingUsers(false)
      }
    }

    fetchPackages()
    fetchUsers()
  }, [])

  useEffect(() => {
    if (lead) {
      setEditedLead({ ...lead })
      setQualificationNotes(lead.qualificationNotes || '')
      setProposal(lead.lastProposal || null)
      setProposalSentAt(lead.proposalSentAt || null)
      setComments(lead.comments || [])
      setNewComment('')
      setActiveTab(0)
    }
  }, [lead])

  if (!editedLead) return null

  const handleFieldChange = (field: keyof Lead, value: string) => {
    setEditedLead({ ...editedLead, [field]: value })
  }

  const handleSave = () => {
    if (editedLead) {
      onSave({ ...editedLead, phone: editedLead.phone ? toE164(editedLead.phone) : '', qualificationNotes })
    }
  }

  const handleQualify = (qualified: boolean) => {
    onQualify(editedLead.id, qualified, qualificationNotes)
  }

  const handleConvert = () => {
    onConvert(editedLead.id)
  }

  const handleGenerateProposal = async () => {
    try {
      setGeneratingProposal(true)
      const result = await leadsApi.generateProposal(editedLead.id)
      setProposal(result.proposal)
      setProposalModalOpen(true)
      showSuccess('Proposal generated successfully')
    } catch (error) {
      console.error('Error generating proposal:', error)
      showError('Failed to generate proposal')
    } finally {
      setGeneratingProposal(false)
    }
  }

  const handleSendProposalEmail = async () => {
    if (!editedLead.email) {
      showError('Lead does not have an email address')
      return
    }
    try {
      setSendingProposal(true)
      const result = await leadsApi.sendProposalEmail(editedLead.id)
      setProposalSentAt(result.sentAt)
      setProposalModalOpen(false)

      // Update local state and notify parent if status changed
      if (result.status && result.status !== editedLead.status) {
        const updatedLead = {
          ...editedLead,
          status: result.status as Lead['status'],
          proposalSentAt: result.sentAt,
        }
        setEditedLead(updatedLead)
        onSave(updatedLead)
      }

      showSuccess(`Proposal sent to ${result.sentTo}`)
    } catch (error) {
      console.error('Error sending proposal email:', error)
      showError(error instanceof Error ? error.message : 'Failed to send proposal email')
    } finally {
      setSendingProposal(false)
    }
  }

  const handleAddComment = async () => {
    if (!editedLead || !newComment.trim()) return

    try {
      setSubmittingComment(true)
      const result = await commentsApi.add(editedLead.id, newComment.trim(), { entityType: 'lead' })
      setComments((prev) => [...prev, result.comment])
      setNewComment('')
      showSuccess('Comment added')
    } catch (error) {
      console.error('Error adding comment:', error)
      showError(error instanceof Error ? error.message : 'Failed to add comment')
    } finally {
      setSubmittingComment(false)
    }
  }

  // Helper functions for comments
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString)
    const now = new Date()
    const diffMs = now.getTime() - date.getTime()
    const diffMins = Math.floor(diffMs / 60000)
    const diffHours = Math.floor(diffMins / 60)
    const diffDays = Math.floor(diffHours / 24)

    if (diffMins < 1) return 'just now'
    if (diffMins < 60) return `${diffMins}m ago`
    if (diffHours < 24) return `${diffHours}h ago`
    if (diffDays < 7) return `${diffDays}d ago`
    return date.toLocaleDateString([], { month: 'short', day: 'numeric' })
  }

  const getInitials = (email: string): string => {
    const parts = email.split('@')[0].split('.')
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase()
    }
    return email.substring(0, 2).toUpperCase()
  }

  const formatName = (email: string): string => {
    const parts = email.split('@')[0].split('.')
    return parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join(' ')
  }

  const stringToColor = (str: string): string => {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash)
    }
    const colors = ['#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b', '#0097a7', '#5d4037']
    return colors[Math.abs(hash) % colors.length]
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: 480,
          backgroundColor: 'background.paper',
          color: 'text.primary',
        },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
        {/* Header */}
        <Box sx={{ p: 3, pb: 0 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">{editedLead.name || 'Lead Details'}</Typography>
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <Chip
              label={STATUS_LABELS[editedLead.status]}
              size="small"
              sx={{
                backgroundColor: STATUS_COLORS[editedLead.status],
                color: 'white',
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Created: {new Date(editedLead.createdAt).toLocaleDateString()}
            </Typography>
          </Box>

          {!infoOnly && (
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab label="Info" sx={{ minWidth: 0, px: 2 }} />
              <Tab label="Qualification" sx={{ minWidth: 0, px: 2 }} />
              <Tab label="Proposal" sx={{ minWidth: 0, px: 2 }} />
              <Tab label="Files" icon={<AttachFileIcon />} iconPosition="start" sx={{ minWidth: 0, px: 2 }} />
            </Tabs>
          )}
        </Box>

        {/* Tab Content */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3 }}>
          {/* Info & Comments Tab */}
          <TabPanel value={activeTab} index={0}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Name"
                value={editedLead.name || ''}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                fullWidth
                size="small"
              />

              <TextField
                label="Email"
                value={editedLead.email || ''}
                onChange={(e) => handleFieldChange('email', e.target.value)}
                fullWidth
                size="small"
              />

              <TextField
                label="Phone"
                value={formatPhoneInput(editedLead.phone || '')}
                onChange={(e) => handleFieldChange('phone', formatPhoneInput(e.target.value))}
                fullWidth
                size="small"
                error={!!editedLead.phone && !isValidPhone(editedLead.phone)}
                helperText={editedLead.phone && !isValidPhone(editedLead.phone) ? 'Enter a valid 10-digit phone number' : ''}
                placeholder="(555) 123-4567"
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  label="Source"
                  select
                  value={editedLead.source || ''}
                  onChange={(e) => handleFieldChange('source', e.target.value)}
                  fullWidth
                  size="small"
                >
                  {['LinkedIn', 'Website', 'Referral', 'Instagram', 'Facebook', 'Cold Outreach', 'Event', 'Other'].map((source) => (
                    <MenuItem key={source} value={source}>
                      {source}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  label="Salary Target"
                  value={editedLead.salaryTarget || ''}
                  onChange={(e) => handleFieldChange('salaryTarget', formatSalaryInput(e.target.value))}
                  fullWidth
                  size="small"
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={editedLead.status}
                    label="Status"
                    onChange={(e) => handleFieldChange('status', e.target.value)}
                  >
                    {LEAD_STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: '50%',
                              backgroundColor: STATUS_COLORS[status as LeadStatus],
                            }}
                          />
                          {STATUS_LABELS[status as LeadStatus]}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth size="small" disabled={loadingUsers}>
                  <InputLabel>Owner</InputLabel>
                  <Select
                    value={editedLead.assignedTo || ''}
                    label="Owner"
                    onChange={(e) => handleFieldChange('assignedTo', e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Unassigned</em>
                    </MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.email} value={user.email}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              <TextField
                label="Notes"
                value={editedLead.notes || ''}
                onChange={(e) => handleFieldChange('notes', e.target.value)}
                multiline
                rows={2}
                fullWidth
                size="small"
              />

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                Comments ({comments.length})
              </Typography>

              {/* Add comment input */}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add a comment..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  multiline
                  maxRows={3}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleAddComment()
                    }
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={submittingComment || !newComment.trim()}
                  onClick={handleAddComment}
                  sx={{ minWidth: 'auto', px: 2 }}
                >
                  {submittingComment ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <SendIcon sx={{ fontSize: 18 }} />
                  )}
                </Button>
              </Box>

              {/* Comments list */}
              {comments.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                  No comments yet
                </Typography>
              ) : (
                <Box sx={{ mt: 1 }}>
                  {[...comments]
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map((comment) => (
                      <Box key={comment.id} sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
                        <Avatar
                          sx={{
                            width: 32,
                            height: 32,
                            fontSize: 12,
                            bgcolor: stringToColor(comment.createdBy),
                          }}
                        >
                          {getInitials(comment.createdBy)}
                        </Avatar>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: 13 }}>
                              {formatName(comment.createdBy)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {formatRelativeTime(comment.createdAt)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontSize: 13 }}>
                            {comment.content}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                </Box>
              )}
            </Box>
          </TabPanel>

          {/* Qualification Tab */}
          <TabPanel value={activeTab} index={1}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                Qualify or disqualify this lead based on your assessment. Add notes to document your
                reasoning.
              </Typography>

              <TextField
                label="Qualification Notes"
                value={qualificationNotes}
                onChange={(e) => setQualificationNotes(e.target.value)}
                multiline
                rows={4}
                fullWidth
                placeholder="Enter notes about lead qualification..."
              />

              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  variant="contained"
                  color="success"
                  onClick={() => handleQualify(true)}
                  disabled={
                    editedLead.status === 'qualified' ||
                    editedLead.status === 'won' ||
                    editedLead.status === 'lost'
                  }
                  fullWidth
                >
                  Qualify
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  onClick={() => handleQualify(false)}
                  disabled={
                    editedLead.status === 'not_qualified' ||
                    editedLead.status === 'won' ||
                    editedLead.status === 'lost'
                  }
                  fullWidth
                >
                  Not Qualified
                </Button>
              </Box>

              {editedLead.status === 'qualified' && (
                <>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="subtitle2" color="text.secondary">
                    Ready to Convert
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    This lead is qualified and ready to become a client. Converting will create a new
                    client record and move them into your client milestones.
                  </Typography>
                  <Button variant="contained" color="secondary" onClick={handleConvert} fullWidth>
                    Convert to Client
                  </Button>
                </>
              )}
            </Box>
          </TabPanel>

          {/* Package & Proposal Tab */}
          <TabPanel value={activeTab} index={2}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Package Selection
              </Typography>

              <FormControl fullWidth disabled={loadingPackages}>
                <InputLabel>Package</InputLabel>
                <Select
                  value={editedLead.selectedPackageId || ''}
                  label="Package"
                  onChange={(e) => handleFieldChange('selectedPackageId', e.target.value)}
                >
                  <MenuItem value="">
                    <em>No package selected</em>
                  </MenuItem>
                  {packages.map((pkg) => (
                    <MenuItem key={pkg.id} value={pkg.id}>
                      {pkg.name} - ${pkg.price.toLocaleString()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ my: 1 }} />

              <Typography variant="subtitle2" color="text.secondary">
                AI Proposal
              </Typography>

              <Typography variant="body2" color="text.secondary">
                Generate a personalized proposal using AI. The proposal will be based on the lead's
                information and selected package.
              </Typography>

              <Tooltip
                title={!editedLead.selectedPackageId ? 'Select a package first to generate a proposal' : ''}
                placement="top"
              >
                <span style={{ width: '100%' }}>
                  <Button
                    variant="outlined"
                    startIcon={generatingProposal ? <CircularProgress size={16} /> : <AIIcon />}
                    onClick={handleGenerateProposal}
                    disabled={generatingProposal || !editedLead.selectedPackageId}
                    fullWidth
                  >
                    {generatingProposal ? 'Generating...' : 'Generate Proposal'}
                  </Button>
                </span>
              </Tooltip>

              {proposal && (
                <>
                  <Button
                    variant="outlined"
                    startIcon={<ViewIcon />}
                    onClick={() => setProposalModalOpen(true)}
                    fullWidth
                  >
                    View Proposal
                  </Button>

                  {proposalSentAt && (
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center' }}>
                      Last sent: {new Date(proposalSentAt).toLocaleString()}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </TabPanel>

          {/* Files Tab */}
          <TabPanel value={activeTab} index={3}>
            <AttachmentsTab entityId={editedLead.id} entityType="lead" />
          </TabPanel>
        </Box>

        {/* Footer */}
        <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button variant="outlined" onClick={onClose} fullWidth>
              Cancel
            </Button>
            <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} fullWidth>
              Save Changes
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Proposal Review Modal */}
      <Dialog
        open={proposalModalOpen}
        onClose={() => setProposalModalOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh' },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">Proposal for {editedLead.name}</Typography>
          <IconButton onClick={() => setProposalModalOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              backgroundColor: 'grey.50',
              minHeight: 400,
            }}
          >
            <Typography
              variant="body1"
              sx={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit', lineHeight: 1.8 }}
            >
              {proposal}
            </Typography>
          </Paper>
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          {!editedLead.email && (
            <Typography variant="caption" color="error" sx={{ mr: 'auto' }}>
              Lead does not have an email address
            </Typography>
          )}
          <Button onClick={() => setProposalModalOpen(false)} variant="outlined">
            Close
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={sendingProposal ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            onClick={handleSendProposalEmail}
            disabled={sendingProposal || !editedLead.email}
          >
            {sendingProposal ? 'Sending...' : 'Email Proposal to Lead'}
          </Button>
        </DialogActions>
      </Dialog>
    </Drawer>
  )
}
